import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  MdOutlineMailOutline,
  MdVisibility,
  MdVisibilityOff,
} from 'react-icons/md';

import { Controller, FormProvider, useForm } from 'react-hook-form';
import { RiAccountCircleLine } from 'react-icons/ri';
import CreatableSelect from 'react-select/creatable';
import tw from 'tailwind-styled-components';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import Checkbox from '../../components/Checkboxes/Checkbox';
import {
  getEducationsService,
  getGroupsService,
  getRolesService,
  getSchoolsService,
  postRegisterService,
} from '../../services/externalv2_2';
import useUser from '../../hooks/user';
import DatePickerInput from '../../components/Input/DatePickerInput';

const selectStyles = {
  control: (provided: any) => ({
    ...provided,
    border: '2px solid #e2e8f0',
    borderRadius: '0.375rem',
    padding: '0.3rem',
  }),
  menu: (provided: any) => ({
    ...provided,
    borderRadius: '0.375rem',
  }),
};

const allowStudentFeild = ['1'];
const allowedParentTeacherFeild = ['2', '3'];

const CreateUser: React.FC = () => {
  const { pathname } = useLocation();
  const user = useUser('token');
  const navigate = useNavigate();
  const swal = withReactContent(Swal);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [mainRole, setMainRole] = useState<string>('admin');
  const [educations, setEducations] = useState<any[]>([]);
  const [schools, setShools] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [roles, setroles] = useState<any[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showCfPassword, setShowCfPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleClickShowCfPassword = () => setShowCfPassword((show) => !show);
  const handleMouseDownCfPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const defaultValues = {
    username: '',
    password: '',
    reTypePassword: '',
    email: '',
    role: '',
    name: '',
    surname: '',
    nick: '',
    dob: '',
    teacherid: '',
    parentid: '',
    gend: 'male',
    edu_reg: null,
    schid: '',
    groupid: null,
    isdylx: false,
  };

  const methods = useForm({
    defaultValues,
  });

  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    setError,
    watch,
    reset,
    formState: { errors },
  } = methods;

  const gendWatch = watch('gend');
  const isdylxWatch = watch('isdylx');
  const roleWatch = watch('role');

  const getEducations = () => {
    getEducationsService().then((data) => {
      setEducations(data.results.educations);
    });
  };

  const getSchools = () => {
    getSchoolsService().then((data) => {
      setShools(data.results.schools);
    });
  };

  const getRoles = () => {
    getRolesService().then((data) => {
      setroles(data.results.roles);
    });
  };

  const getGroups = () => {
    if (['teacher'].includes(mainRole))
      getGroupsService().then((data) => {
        setGroups(data.results.groups);
      });
  };

  const handleGenderChanged = (e: React.FormEvent<HTMLInputElement>) => {
    setValue('gend', e.currentTarget.value);
  };

  const handleIsdylxChanged = (_: React.FormEvent<HTMLInputElement>) => {
    setValue('isdylx', !isdylxWatch);
  };

  const onSubmitHandler = useCallback(() => {
    const values = getValues();

    setIsLoading(true);

    postRegisterService({
      username: values.username,
      password: values.password,
      email: values.email,
      role: Number(values.role),
      name: values.name,
      surname: values.surname,
      nick: values.nick,
      dob: values.dob,
      gend: values.gend,
      isdylx: values.isdylx,
      teacherid: values.teacherid || undefined,
      parentid: values.parentid || undefined,
      edu_reg: values.edu_reg ? Number(values.edu_reg) : undefined,
      schid: values.schid || undefined,
      groupid: values.groupid || undefined,
    })
      .then((json) => {
        setIsLoading(false);
        setIsSuccess(false);
        if (json.resultCode === '20100') {
          swal
            .fire({
              title: 'บันทึกข้อมูลสำเร็จ',
              text: 'ข้อมูลของคุณถูกบันทึกแล้ว',
              icon: 'success',
              confirmButtonText: 'ตกลง',
            })
            .then(() => {
              reset(defaultValues);
              navigate(-1);
            });
        } else {
          const message =
            json.resultCode === '40900'
              ? 'บัญชีผู้ใช้งานนี้มีอยู่แล้ว'
              : json.message;

          setError('username', {
            type: json.resultCode,
            message,
          });
        }
      })
      .catch((error) => console.error(error));
  }, [getValues]);

  useEffect(() => {
    switch (pathname) {
      case '/sornthai/dashboard-teacher/create-student':
        setValue('role', '1');
        setValue('teacherid', user._id);
        setMainRole('teacher');
        break;

      case '/sornthai/dashboard-parent/create-student':
        setValue('role', '1');
        setValue('parentid', user._id);
        setMainRole('parent');
        break;

      default:
        break;
    }
  }, [pathname]);

  useEffect(getEducations, []);
  useEffect(getSchools, []);
  useEffect(getRoles, []);
  useEffect(getGroups, [mainRole]);

  return (
    <div className="flex">
      <div className="rounded-xl border border-stroke bg-white shadow-default w-full">
        <div className="flex flex-wrap items-center justify-center">
          <div className="p-4 sm:p-12.5 xl:p-17.5  w-full">
            <h2 className="mb-9 text-2xl font-bold text-black sm:text-title-xl2">
              สร้างบัญชีผู้ใช้งาน
            </h2>
            {isSuccess && (
              <div className="mb-8 flex w-full border-l-6 border-[#34D399] bg-[#34D399] bg-opacity-[15%] px-4 py-3 shadow-md dark:bg-[#1B1B24] dark:bg-opacity-30 ">
                <div className="mr-5 flex h-9 w-full max-w-[36px] items-center justify-center rounded-lg bg-[#34D399]">
                  <svg
                    width="16"
                    height="12"
                    viewBox="0 0 16 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.2984 0.826822L15.2868 0.811827L15.2741 0.797751C14.9173 0.401867 14.3238 0.400754 13.9657 0.794406L5.91888 9.45376L2.05667 5.2868C1.69856 4.89287 1.10487 4.89389 0.747996 5.28987C0.417335 5.65675 0.417335 6.22337 0.747996 6.59026L0.747959 6.59029L0.752701 6.59541L4.86742 11.0348C5.14445 11.3405 5.52858 11.5 5.89581 11.5C6.29242 11.5 6.65178 11.3355 6.92401 11.035L15.2162 2.11161C15.5833 1.74452 15.576 1.18615 15.2984 0.826822Z"
                      fill="white"
                      stroke="white"
                    ></path>
                  </svg>
                </div>
                <div className="w-full">
                  <h5 className="mb-0 text-lg font-semibold text-black dark:text-[#34D399] ">
                    สร้างบัญชีผู้ใช้งานสำเร็จ
                  </h5>
                  <p className="text-base leading-relaxed text-body">
                    กลับสู่
                    <Link
                      to="javascript:void(0)"
                      onClick={() => navigate(-1)}
                      className="ml-1 text-primary"
                    >
                      หน้าหลัก
                    </Link>
                  </p>
                </div>
              </div>
            )}

            <FormProvider {...methods}>
              <form
                onSubmit={handleSubmit(onSubmitHandler)}
                className="grid grid-cols-1 gap-4 xl:grid-cols-2"
              >
                <div className="grid grid-cols-1">
                  <label className="mb-2.5 block font-medium text-black dark:text-white">
                    บัญชีผู้ใช้งาน
                    <span className="text-meta-1">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="ระบุบัญชีผู้ใช้งาน"
                      className={`${!!errors['username'] && 'border-meta-1'}`}
                      {...register('username', {
                        required: 'กรุณาระบุบัญชีผู้ใช้งาน',
                      })}
                    />

                    <span className="absolute right-4 top-4">
                      <RiAccountCircleLine
                        fontSize={25}
                        className="opacity-50"
                      />
                    </span>
                  </div>
                  {errors.username && (
                    <p className="text-danger">
                      {errors?.username?.message as string}
                    </p>
                  )}
                </div>

                <div />

                <div className="">
                  <label className="mb-2.5 block font-medium text-black dark:text-white">
                    รหัสผ่าน
                    <span className="text-meta-1">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="ระบุรหัสผ่าน"
                      className={`${!!errors['password'] && 'border-meta-1'}`}
                      {...register('password', {
                        required: 'กรุณาระบุรหัสผ่าน',
                      })}
                    />

                    <span className="absolute right-4 top-4">
                      <button
                        type="button"
                        className="text-[25px]"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                      </button>
                    </span>
                  </div>
                  {errors.password && (
                    <p className="text-danger">
                      {errors?.password?.message as string}
                    </p>
                  )}
                </div>

                <div className="">
                  <label className="mb-2.5 block font-medium text-black dark:text-white">
                    ยืนยันรหัสผ่าน
                    <span className="text-meta-1">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type={showCfPassword ? 'text' : 'password'}
                      placeholder="ระบุยืนยันรหัสผ่าน"
                      className={`${
                        !!errors['reTypePassword'] && 'border-meta-1'
                      }`}
                      {...register('reTypePassword', {
                        required: 'กรุณาระบุยืนยันรหัสผ่าน',
                        validate: (val: string) => {
                          if (watch('password') != val) {
                            return 'รหัสผ่านของคุณไม่ตรงกัน';
                          }
                        },
                      })}
                    />

                    <span className="absolute right-4 top-4">
                      <button
                        type="button"
                        className="text-[25px]"
                        onClick={handleClickShowCfPassword}
                        onMouseDown={handleMouseDownCfPassword}
                      >
                        {showCfPassword ? (
                          <MdVisibilityOff />
                        ) : (
                          <MdVisibility />
                        )}
                      </button>
                    </span>
                  </div>
                  {errors.reTypePassword && (
                    <p className="text-danger">
                      {errors?.reTypePassword?.message as string}
                    </p>
                  )}
                </div>

                {allowedParentTeacherFeild.includes(roleWatch) && (
                  <>
                    <div className="grid grid-cols-1">
                      <label className="mb-2.5 block font-medium text-black dark:text-white">
                        อีเมล
                        <span className="text-meta-1">*</span>
                      </label>
                      <div className="relative">
                        <Input
                          type="email"
                          placeholder="ระบุอีเมล"
                          className={`${!!errors['email'] && 'border-meta-1'}`}
                          {...register('email', {
                            required: 'กรุณาระบุอีเมล',
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'รูปแบบอีเมลไม่ถูกต้อง',
                            },
                          })}
                        />

                        <span className="absolute right-4 top-4">
                          <MdOutlineMailOutline
                            fontSize={25}
                            className="opacity-50"
                          />
                        </span>
                      </div>
                      {errors.email && (
                        <p className="text-danger">
                          {errors?.email?.message as string}
                        </p>
                      )}
                    </div>
                    <div />
                  </>
                )}

                <div className="">
                  <label className="mb-2.5 block font-medium text-black dark:text-white">
                    ชื่อจริง
                    <span className="text-meta-1">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="ระบุชื่อจริง"
                    className={`${!!errors['name'] && 'border-meta-1'}`}
                    {...register('name', {
                      required: 'กรุณาระบุชื่อจริง',
                    })}
                  />
                  {errors.name && (
                    <p className="text-danger">
                      {errors?.name?.message as string}
                    </p>
                  )}
                </div>

                <div className="">
                  <label className="mb-2.5 block font-medium text-black dark:text-white">
                    นามสกุล
                    <span className="text-meta-1">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="ระบุนามสกุล"
                    className={`${!!errors['surname'] && 'border-meta-1'}`}
                    {...register('surname', {
                      required: 'กรุณาระบุนามสกุล',
                    })}
                  />
                  {errors.surname && (
                    <p className="text-danger">
                      {errors?.surname?.message as string}
                    </p>
                  )}
                </div>

                <div className="">
                  <label className="mb-2.5 block font-medium text-black dark:text-white">
                    ชื่อเล่น
                  </label>
                  <Input
                    type="text"
                    placeholder="ระบุชื่อเล่น"
                    {...register('nick')}
                  />
                </div>

                <div className="">
                  <label className="mb-2.5 block font-medium text-black dark:text-white">
                    วันเกิด
                    <span className="text-meta-1">*</span>
                  </label>
                  <DatePickerInput
                    fieldKey="dob"
                    error={errors['dob']}
                    errorMessage="กรุณาระบุวันเกิด"
                  />
                </div>

                {['admin'].includes(mainRole) && (
                  <div className="">
                    <label className="mb-2.5 block font-medium text-black dark:text-white">
                      บทบาท
                      <span className="text-meta-1">*</span>
                    </label>
                    <div className="relative z-20 bg-white dark:bg-form-input">
                      <SelectCommon
                        className={`${!!errors['role'] && 'border-meta-1'} `}
                        {...register('role', { required: 'กรุณาเลือกบทบาท' })}
                      >
                        <option value="" disabled selected>
                          เลือกบทบาท
                        </option>
                        {roles.map((item: any) => (
                          <option
                            key={item._id}
                            value={item.roleid}
                            className="text-body dark:text-bodydark"
                          >
                            {item.name_th}
                          </option>
                        ))}
                      </SelectCommon>

                      <span className="absolute top-1/2 right-4 z-10 -translate-y-1/2">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g opacity="0.8">
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z"
                              fill="#637381"
                            ></path>
                          </g>
                        </svg>
                      </span>
                    </div>
                    {errors.role && (
                      <p className="text-danger">
                        {errors?.role?.message as string}
                      </p>
                    )}
                  </div>
                )}

                {allowStudentFeild.includes(roleWatch) && (
                  <div className="">
                    <label className="mb-2.5 block font-medium text-black dark:text-white">
                      โรงเรียน
                      <span className="text-meta-1">*</span>
                    </label>
                    <Controller
                      name="schid"
                      control={control}
                      rules={{ required: 'กรุณาระบุโรงเรียน' }}
                      render={({ field }) => {
                        const options = schools.map((sch) => ({
                          value: sch.sch_name,
                          label: sch.sch_name,
                        }));
                        const selected =
                          options.find((o) => o.value === field.value) ||
                          (field.value
                            ? { value: field.value, label: field.value }
                            : null);

                        return (
                          <CreatableSelect
                            options={options}
                            value={selected}
                            onChange={(option) =>
                              field.onChange(option?.value ?? '')
                            }
                            onCreateOption={(inputValue) =>
                              field.onChange(inputValue)
                            }
                            placeholder="เลือกหรือระบุโรงเรียน"
                            styles={selectStyles}
                            className="z-30"
                            isSearchable
                            isClearable
                            formatCreateLabel={(inputValue) =>
                              `เพิ่ม "${inputValue}"`
                            }
                            noOptionsMessage={() =>
                              'ไม่พบโรงเรียน - พิมพ์เพื่อเพิ่มใหม่'
                            }
                          />
                        );
                      }}
                    />
                    {errors.schid && (
                      <p className="text-danger">
                        {errors?.schid?.message as string}
                      </p>
                    )}
                  </div>
                )}

                {allowStudentFeild.includes(roleWatch) && (
                  <div className="">
                    <label className="mb-2.5 block font-medium text-black dark:text-white">
                      ระดับการศึกษา
                      <span className="text-meta-1">*</span>
                    </label>
                    <div className="relative z-20 bg-white dark:bg-form-input">
                      <SelectCommon
                        className={`${!!errors['edu_reg'] && 'border-meta-1'} `}
                        {...register('edu_reg', {
                          required: 'กรุณาเลือกระดับการศึกษา',
                        })}
                      >
                        <option value="" disabled selected>
                          เลือกระดับการศึกษา
                        </option>
                        {educations.map((item: any) => (
                          <option
                            key={item.educationid}
                            value={item.educationid}
                            className="text-body dark:text-bodydark"
                          >
                            {item.name_th}
                          </option>
                        ))}
                      </SelectCommon>

                      <span className="absolute top-1/2 right-4 z-10 -translate-y-1/2">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g opacity="0.8">
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z"
                              fill="#637381"
                            ></path>
                          </g>
                        </svg>
                      </span>
                    </div>
                    {errors.edu_reg && (
                      <p className="text-danger">
                        {errors?.edu_reg?.message as string}
                      </p>
                    )}
                  </div>
                )}

                {['teacher'].includes(mainRole) && (
                  <div className="">
                    <label className="mb-2.5 block font-medium text-black dark:text-white">
                      กลุ่ม
                    </label>
                    <div className="relative z-20 bg-white dark:bg-form-input">
                      <SelectCommon {...register('groupid')}>
                        <option value="" disabled selected>
                          เลือกกลุ่ม
                        </option>
                        {groups.map((item: any) => (
                          <option
                            key={item._id}
                            value={item._id}
                            className="text-body dark:text-bodydark"
                          >
                            {item.group_name}
                          </option>
                        ))}
                      </SelectCommon>

                      <span className="absolute top-1/2 right-4 z-10 -translate-y-1/2">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g opacity="0.8">
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M5.29289 8.29289C5.68342 7.90237 6.31658 7.90237 6.70711 8.29289L12 13.5858L17.2929 8.29289C17.6834 7.90237 18.3166 7.90237 18.7071 8.29289C19.0976 8.68342 19.0976 9.31658 18.7071 9.70711L12.7071 15.7071C12.3166 16.0976 11.6834 16.0976 11.2929 15.7071L5.29289 9.70711C4.90237 9.31658 4.90237 8.68342 5.29289 8.29289Z"
                              fill="#637381"
                            ></path>
                          </g>
                        </svg>
                      </span>
                    </div>
                    {errors.role && (
                      <p className="text-danger">
                        {errors?.role?.message as string}
                      </p>
                    )}
                  </div>
                )}

                <div className="">
                  <label className="mb-2.5 block font-medium text-black dark:text-white">
                    เพศ
                    <span className="text-meta-1">*</span>
                  </label>
                  <div className="flex gap-4">
                    <Checkbox
                      label="ชาย"
                      id="male"
                      isChecked={gendWatch === 'male'}
                      onChange={handleGenderChanged}
                    />
                    <Checkbox
                      label="หญิง"
                      id="female"
                      isChecked={gendWatch === 'female'}
                      onChange={handleGenderChanged}
                    />
                  </div>
                </div>

                {allowStudentFeild.includes(roleWatch) && (
                  <div className="mb-6">
                    <label className="mb-2.5 block font-medium text-black dark:text-white">
                      ความบกพร่องทางการอ่าน
                    </label>
                    <div className="flex gap-4">
                      <Checkbox
                        label="มี"
                        id="isdylx"
                        isChecked={isdylxWatch}
                        onChange={handleIsdylxChanged}
                      />
                    </div>
                  </div>
                )}

                <div className="flex gap-4 mb-5">
                  <input
                    type="submit"
                    value="สร้างบัญชีผู้ใช้งาน"
                    className="w-full cursor-pointer rounded-lg border border-primary bg-primary p-4 text-white transition hover:bg-opacity-90"
                    disabled={isLoading}
                  />
                  <input
                    type="button"
                    onClick={() => navigate(-1)}
                    value="ย้อนกลับ"
                    className="w-full cursor-pointer rounded-lg border border-primary  p-4 text-primary transition hover:bg-opacity-90 hover:text-white hover:bg-primary"
                  />
                </div>
              </form>
            </FormProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

const Input = tw.input`w-full rounded-lg border border-stroke bg-transparent py-4 pl-6 pr-10 text-black outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark`;

const SelectCommon = tw.select`relative z-20 w-full appearance-none rounded border border-stroke bg-transparent py-3 px-6 outline-none transition focus:border-primary active:border-primary`;

export default CreateUser;
